import dayjs from "dayjs"

import { SLATE } from "../../../../../constants/palette.ts"
import { LEAD_STATUSES } from "../../../../Lead/constants/lead-statuses.ts"

import { OPERATIONAL_STATES } from "../../../../Lead/constants/operational-states.ts"
import {
  clearedActiveFilters,
  matchesActiveFilters
} from "../../../redux/leads.constants.ts"
import type { LeadsFilters } from "../../../redux/leads.types.ts"

/**
 * The design's "build powerful views in seconds" row: each chip is a whole
 * filter set, not an extra condition on the current one. Presets therefore
 * clear the sibling filters rather than merging onto whatever is already set —
 * merging would make a chip's meaning depend on the order it was clicked in.
 *
 * `statsKey` names the count the chip displays, which arrives with the KPI
 * payload. A preset with no `statsKey` shows no count rather than a wrong one.
 */
export interface LeadSmartFilter {
  key: string
  /** i18n key; the chip's own copy lives in the bundle, not here. */
  labelKey: string
  statsKey?: string
  /** The design gives each chip a glyph and tints its count to match. */
  icon: "spark" | "flame" | "funnel" | "clock" | "arrow-down"
  tint: string
  /**
   * The preset names the signed-in user, so it cannot be built — or read as
   * selected — without one. Left unguarded it degrades to "no filters at all",
   * which matches an unfiltered page and lights the chip up on load.
   */
  requiresUser?: boolean
  build: (context: { userId?: string | null }) => Partial<LeadsFilters>
}

const withCleared = (
  filters: Partial<LeadsFilters>
): Partial<LeadsFilters> => ({
  ...clearedActiveFilters(),
  ...filters
})

export const LEAD_SMART_FILTERS: LeadSmartFilter[] = [
  {
    key: "my_active",
    labelKey: "leadsTable.smartFilters.myActiveLeads",
    statsKey: "my_active_leads",
    icon: "spark",
    tint: "#3B82F6",
    requiresUser: true,
    build: ({ userId }) =>
      withCleared(userId ? { sales_manager: [userId] } : {})
  },
  {
    key: "hot_follow_up",
    labelKey: "leadsTable.smartFilters.hotFollowUp",
    statsKey: "hot_follow_up",
    icon: "flame",
    tint: "#EF4444",
    build: () =>
      withCleared({
        operational_states: [
          OPERATIONAL_STATES.HOT,
          OPERATIONAL_STATES.FOLLOW_UP
        ]
      })
  },
  {
    key: "flagged_unscheduled",
    labelKey: "leadsTable.smartFilters.flaggedUnscheduled",
    statsKey: "flagged_unscheduled",
    icon: "funnel",
    tint: SLATE.text,
    build: () =>
      withCleared({
        operational_states: [OPERATIONAL_STATES.FOLLOW_UP],
        follow_up_scheduled: "false"
      })
  },
  {
    key: "new_leads",
    labelKey: "leadsTable.smartFilters.newLeads",
    statsKey: "new_leads",
    icon: "arrow-down",
    tint: "#16A34A",
    build: () => withCleared({ status: [LEAD_STATUSES.NEW] })
  },
  {
    key: "today_leads",
    labelKey: "leadsTable.smartFilters.todayLeads",
    statsKey: "today_leads",
    icon: "clock",
    tint: "#8B5CF6",
    build: () => withCleared({ created_after: dayjs().format("YYYY-MM-DD") })
  }
]

/**
 * A chip reads as selected when the filters it would write are already the ones
 * in force. Comparing the built preset against the live filters — rather than
 * tracking the last chip clicked — keeps the row honest when the user edits a
 * filter by hand afterwards.
 */
export const isSmartFilterUsable = (
  preset: LeadSmartFilter,
  context: { userId?: string | null }
): boolean => !preset.requiresUser || !!context.userId

export const isSmartFilterActive = (
  preset: LeadSmartFilter,
  filters: LeadsFilters,
  context: { userId?: string | null }
): boolean => {
  // Without the user it names, this preset builds to an empty filter set, which
  // is exactly what an unfiltered page already is — so it would read as
  // selected on load while filtering nothing.
  if (!isSmartFilterUsable(preset, context)) return false

  return matchesActiveFilters(preset.build(context), filters)
}
